/** Client-side mirrors of API crew matching — keep in sync with apps/api crewAssign.ts */

import { catalogCrewMin } from "./vehicleCrewMin";

const FIRE_CREW = ["apparatus_driver", "group_leader", "firefighter"] as const;
const POLICE_CREW = ["officer"] as const;
const EMS_CREW = ["medic"] as const;
const THW_CREW = ["thw_helper", "group_leader", "apparatus_driver"] as const;
const COMMAND_CREW = ["group_leader", "dispatcher", "firefighter"] as const;
const BOAT_CREW = ["boat_crew", "officer"] as const;

const BY_TYPE: Record<string, readonly string[]> = {
  LF: FIRE_CREW,
  HLF: FIRE_CREW,
  TSF: FIRE_CREW,
  TLF: FIRE_CREW,
  RW: FIRE_CREW,
  SchlauchW: FIRE_CREW,
  "GW-Oel": FIRE_CREW,
  "GW-Atemschutz": FIRE_CREW,
  DLK: FIRE_CREW,
  MTF: FIRE_CREW,
  WLF: FIRE_CREW,
  "AB-Ruest": FIRE_CREW,
  "AB-Schlauch": FIRE_CREW,
  ELW: COMMAND_CREW,
  ELW1: COMMAND_CREW,
  ELW2: COMMAND_CREW,
  FuStW: POLICE_CREW,
  "FuStW-DGL": POLICE_CREW,
  "BAB-FuStW": POLICE_CREW,
  Motorrad: POLICE_CREW,
  RTW: EMS_CREW,
  KTW: EMS_CREW,
  NEF: EMS_CREW,
  NHW: EMS_CREW,
  GKW: THW_CREW,
  MTW: THW_CREW,
};

export function preferredRolesForVehicle(type: string): string[] {
  if (BY_TYPE[type]) return [...BY_TYPE[type]];
  if (/boot|wsp|boat/i.test(type)) return [...BOAT_CREW];
  if (/FuSt|police|Streifen/i.test(type)) return [...POLICE_CREW];
  if (/RTW|KTW|NEF|Rettung/i.test(type)) return [...EMS_CREW];
  if (/GKW|THW/i.test(type)) return [...THW_CREW];
  if (/ELW/i.test(type)) return [...COMMAND_CREW];
  return [...FIRE_CREW];
}

export function roleFitsVehicle(role: string, vehicleType: string): boolean {
  return preferredRolesForVehicle(vehicleType).includes(role);
}

export function crewMismatchWarning(
  role: string,
  skill: number,
  vehicleType: string,
): string | null {
  if (!roleFitsVehicle(role, vehicleType)) {
    return "Rolle passt nicht zum Fahrzeugtyp";
  }
  if (skill < 35) return "Geringe Qualifikation — Lehrgang empfohlen";
  return null;
}

/** Assigned personnel count on a vehicle (vehicleId link). */
export function assignedCrewCount(
  vehicleId: string,
  personnel: Array<{ vehicleId?: string | null }>,
): number {
  return personnel.filter((p) => p.vehicleId === vehicleId).length;
}

export type CrewVehicleRef = {
  id: string;
  type?: string;
  crew?: number | null;
  crewMin?: number | null;
};

export function resolveCrewMin(vehicle: CrewVehicleRef): number {
  if (typeof vehicle.crewMin === "number" && vehicle.crewMin >= 0) {
    const seats = Math.max(0, vehicle.crew ?? vehicle.crewMin);
    return Math.min(seats, vehicle.crewMin);
  }
  const seats = Math.max(0, vehicle.crew ?? 0);
  return catalogCrewMin(vehicle.type, seats);
}

/** Below Mindestbesatzung → cannot alarm. */
export function vehicleNeedsCrew(
  vehicle: CrewVehicleRef,
  personnel: Array<{ vehicleId?: string | null }>,
): boolean {
  const min = resolveCrewMin(vehicle);
  if (min <= 0) return false;
  return assignedCrewCount(vehicle.id, personnel) < min;
}

export function crewShortageLabel(
  vehicle: CrewVehicleRef,
  personnel: Array<{ vehicleId?: string | null }>,
): string | null {
  const min = resolveCrewMin(vehicle);
  if (min <= 0) return null;
  const current = assignedCrewCount(vehicle.id, personnel);
  if (current >= min) return null;
  return `Mindestbesatzung ${min} (aktuell ${current})`;
}

export function vehicleHasAlarmCrew(
  vehicle: CrewVehicleRef,
  personnel: Array<{ vehicleId?: string | null }>,
): boolean {
  return !vehicleNeedsCrew(vehicle, personnel);
}

/** Manuell auf Fahrzeug legbar (idle oder außer Dienst — Dienst wird beim Zuweisen gesetzt). */
export function isPersonAssignable(status: string): boolean {
  return status === "idle" || status === "off_duty";
}

/** Für Auto-Zuweisung — gleiche Regeln wie API crewMatchScore. */
export function isPersonAutoAssignable(person: {
  status: string;
  vehicleId?: string | null;
}): boolean {
  if (person.vehicleId) return false;
  if (person.status === "sick" || person.status === "training") return false;
  if (person.status === "assigned") return false;
  return person.status === "idle" || person.status === "off_duty";
}
